import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import type {
  ProactiveGreetingPhrase,
  ProactiveGreetingQuickReply,
  WidgetSettingsResponse,
} from '@teo/widget-shared';
import {
  getLocalizedText,
  resolveProactiveGreetingQuickReplies as resolveGreetingQuickReplies,
} from '@teo/widget-shared';
import {
  createMockSendMessage,
  createMockSendImageMessage,
  getGreetingOfferState,
  getProactiveGreetingCache,
  getWidgetSession,
  isGreetingIdCommitted,
  isPopupDebugEnabled,
  logScroll,
  markGreetingOfferShownFromEntry,
  acknowledgeLauncherGreetingPhrases,
  markHumanReadyFromUi,
  markPopupConvertedToChat,
  reportWidgetEvent,
  resolveGreetingIdFromEntry,
  subscribeConnectivity,
  type ChatOpenSource,
  type RealtimeConnectionPhase,
  type ReverbClientConfig,
  type SendMessageFn,
  type SendImageMessageFn,
} from '@teo/widget-core';
import { ChatProvider, useChat } from './chat/ChatProvider.js';
import { WidgetProvider, useWidget } from './context/WidgetContext.js';
import { useLoadThreadHistory } from './hooks/useLoadThreadHistory.js';
import { useLauncherUnread } from './hooks/useLauncherUnread.js';
import { useContextPopups } from './hooks/useContextPopups.js';
import { useMobileVisualViewport } from './hooks/useMobileVisualViewport.js';
import { useProactiveGreeting } from './hooks/useProactiveGreeting.js';
import { useWidgetRealtime } from './hooks/useWidgetRealtime.js';
import { LauncherButton } from './components/LauncherButton.js';
import { ContextPopup } from './components/ContextPopup.js';
import { ChatWindow, type ChatWindowProps } from './components/ChatWindow.js';
import type { ConnectionBannerState } from './components/ConnectionBanner.js';
import type { HistoryStatus } from './hooks/useLoadThreadHistory.js';
import {
  messagesContainPhraseSequence,
  type DemoMessage,
} from './components/MessageList.js';
import { DraggableLauncher } from './mobile/DraggableLauncher.js';
import { EdgeDockTab } from './mobile/EdgeDockTab.js';
import {
  expandedLauncherPos,
  type LauncherRect,
} from './mobile/launcher-geometry.js';
import { resolveGreetingPlacement } from './mobile/resolve-greeting-placement.js';
import { useIsMobileViewport } from './mobile/useIsMobileViewport.js';
import widgetCss from './styles/widget.css?inline';

const defaultSendMessage = createMockSendMessage();
const defaultSendImageMessage = createMockSendImageMessage();

type PendingChatOffer = {
  pathname: string;
  phrases: ProactiveGreetingPhrase[];
  baseTimeMs: number;
  greetingId: string;
};

export type WidgetShellProps = {
  rawSettings: WidgetSettingsResponse;
  initialLocale?: string;
  chatOpen?: boolean;
  onChatOpenChange?: (open: boolean) => void;
  popupVisible?: boolean;
  popupMessage?: string;
  onPopupClose?: () => void;
  unreadCount?: number;
  /** Controlled message list (Storybook). Skips live optimistic send. */
  messages?: DemoMessage[];
  threadId?: number | string | null;
  sendMessage?: SendMessageFn;
  sendImageMessage?: SendImageMessageFn;
  apiBase?: string;
  reverbConfig?: ReverbClientConfig | null;
  mock?: boolean;
  initialMessages?: DemoMessage[];
  showManager?: boolean;
  isTyping?: boolean;
  isRecording?: boolean;
  showWelcome?: boolean;
  historyStatus?: HistoryStatus;
  embedded?: boolean;
  popupDebug?: boolean;
} & Pick<ChatWindowProps, 'onToggleRecording'>;

function WidgetShellInner({
  chatOpen: chatOpenProp,
  onChatOpenChange,
  popupVisible: popupVisibleProp,
  popupMessage,
  onPopupClose,
  unreadCount: unreadCountProp = undefined,
  showManager = false,
  isTyping = false,
  isRecording = false,
  showWelcome = true,
  historyStatus: historyStatusOverride,
  embedded = false,
  popupDebug = false,
  onToggleRecording,
  apiBase,
  threadId,
  reverbConfig,
  mock = false,
  loadThreadHistory = true,
}: Omit<
  WidgetShellProps,
  | 'rawSettings'
  | 'initialLocale'
  | 'messages'
  | 'threadId'
  | 'sendMessage'
  | 'sendImageMessage'
  | 'apiBase'
  | 'reverbConfig'
  | 'initialMessages'
> & {
  apiBase?: string;
  threadId?: number | string | null;
  reverbConfig?: ReverbClientConfig | null;
  loadThreadHistory?: boolean;
  mock?: boolean;
}) {
  const { settings, cssVars, locale } = useWidget();
  const { messages, send, retry, isTyping: isBotTyping, activeThreadId } = useChat();
  const [chatOpenInternal, setChatOpenInternal] = useState(false);
  const [resolvedThreadId, setResolvedThreadId] = useState(threadId ?? null);
  const [browserOnline, setBrowserOnline] = useState(true);
  const [realtimePhase, setRealtimePhase] =
    useState<RealtimeConnectionPhase>('idle');

  const effectiveThreadId =
    (activeThreadId != null && Number(activeThreadId) > 0 ? activeThreadId : null) ??
    (resolvedThreadId != null && Number(resolvedThreadId) > 0 ? resolvedThreadId : null);

  const chatOpen = chatOpenProp ?? chatOpenInternal;
  const widgetRootRef = useMobileVisualViewport(chatOpen && !embedded);

  useEffect(() => {
    logScroll('shell', chatOpen ? 'chat opened' : 'chat closed', {
      messageCount: messages.length,
      effectiveThreadId,
    });
  }, [chatOpen, messages.length, effectiveThreadId]);

  useEffect(() => {
    if (threadId != null && Number(threadId) > 0) {
      setResolvedThreadId(threadId);
    }
  }, [threadId]);

  useEffect(() => {
    const sessionThreadId = getWidgetSession()?.threadId;
    if (sessionThreadId != null && Number(sessionThreadId) > 0) {
      setResolvedThreadId(sessionThreadId);
    }
  }, []);

  useEffect(() => {
    if (activeThreadId != null && Number(activeThreadId) > 0) {
      setResolvedThreadId(activeThreadId);
    }
  }, [activeThreadId]);

  useEffect(() => {
    return subscribeConnectivity(setBrowserOnline);
  }, []);

  useWidgetRealtime({
    apiBase,
    reverbConfig,
    threadId: effectiveThreadId,
    enabled: loadThreadHistory && Boolean(reverbConfig),
    onThreadIdChange: setResolvedThreadId,
    onConnectionStateChange: setRealtimePhase,
  });

  const connectionBanner = useMemo((): ConnectionBannerState | null => {
    if (!browserOnline) return 'offline';
    if (!reverbConfig) return null;
    if (realtimePhase === 'connecting') return 'connecting';
    if (
      realtimePhase === 'disconnected' ||
      realtimePhase === 'unavailable'
    ) {
      return 'disconnected';
    }
    return null;
  }, [browserOnline, realtimePhase, reverbConfig]);

  const { historyStatus, retryHistory } = useLoadThreadHistory({
    chatOpen,
    apiBase,
    threadId: effectiveThreadId,
    enabled: loadThreadHistory,
    onThreadIdResolved: setResolvedThreadId,
  });

  const resolvedHistoryStatus = historyStatusOverride ?? historyStatus;

  const launcherUnread = useLauncherUnread({
    messages,
    chatOpen,
    historyStatus: resolvedHistoryStatus,
    loadThreadHistory,
    apiBase,
    threadId: effectiveThreadId,
    controlledUnread: unreadCountProp,
  });
  const setChatOpen = useCallback(
    (open: boolean, source?: ChatOpenSource) => {
      // Ignore non-string sources (e.g. React synthetic events if a handler is wired as onClick={fn}).
      const openSource: ChatOpenSource =
        typeof source === 'string' ? source : 'external';
      if (open && !chatOpen) {
        markHumanReadyFromUi('launcher');
        reportWidgetEvent('chat_opened', {
          source: openSource,
        });
      } else if (!open && chatOpen) {
        reportWidgetEvent('chat_closed', {});
      }
      onChatOpenChange?.(open);
      if (chatOpenProp === undefined) setChatOpenInternal(open);
    },
    [chatOpen, chatOpenProp, onChatOpenChange],
  );

  const autoPopupsEnabled =
    popupVisibleProp === undefined && popupMessage === undefined;

  const greetingSlug = useMemo(() => {
    const session = getWidgetSession();
    if (session?.slug) return session.slug;
    if (mock) return 'teo-playground-mock';
    return null;
  }, [mock]);

  const { chatWelcomePhrases, pathname: greetingPathname, entry: greetingEntry } =
    useProactiveGreeting({
      enabled: !embedded && Boolean(apiBase),
      apiBase,
      slug: greetingSlug,
      mock,
    });

  const knownGreetingPhrases = greetingEntry?.greetingPhrases ?? chatWelcomePhrases;
  const quickReplies = useMemo(() => {
    if (
      !settings.content.proactiveGreetingEnabled ||
      !settings.content.proactiveGreetingQuickReplies
    ) {
      return [];
    }

    if (settings.content.proactiveGreetingQuickReplySource === 'custom') {
      const text = getLocalizedText(
        settings.content.proactiveGreetingCustomQuickReply,
        locale,
      ).trim();
      if (!text) return [];
      return [
        {
          id: 'custom',
          label: text,
          message: text,
        },
      ];
    }

    return resolveGreetingQuickReplies({
      response: greetingEntry?.response,
      locale,
      maxCount: settings.content.proactiveGreetingQuickReplyCount,
    });
  }, [
    greetingEntry?.response,
    locale,
    settings.content.proactiveGreetingCustomQuickReply,
    settings.content.proactiveGreetingEnabled,
    settings.content.proactiveGreetingQuickReplies,
    settings.content.proactiveGreetingQuickReplyCount,
    settings.content.proactiveGreetingQuickReplySource,
  ]);

  const logGreetingOffer = useCallback((...args: unknown[]) => {
    if (!popupDebug && !isPopupDebugEnabled()) return;
    console.info('[Teo GreetingOffer]', ...args);
  }, [popupDebug]);

  const isFirstDialog =
    effectiveThreadId == null || Number(effectiveThreadId) <= 0;

  const [pendingChatOffer, setPendingChatOffer] =
    useState<PendingChatOffer | null>(null);

  const capturePendingOfferFromCache = useCallback(
    (options?: { forceShownAt?: number; reason?: string }) => {
      const reason = options?.reason ?? 'capture';
      if (!greetingSlug) {
        logGreetingOffer(reason, 'skip - no slug');
        return null;
      }

      const firstDialog =
        effectiveThreadId == null || Number(effectiveThreadId) <= 0;
      if (
        !firstDialog &&
        settings.content.proactiveGreetingMode === 'first_dialog_reengage'
      ) {
        const offer = getGreetingOfferState(greetingSlug, greetingPathname);
        const fromPopup =
          reason === 'popup-visible' ||
          reason === 'auto-open' ||
          reason === 'dwell-reengage';
        if (!fromPopup && offer?.status !== 'shown') {
          logGreetingOffer(reason, 'skip - mid-thread without reengage offer', {
            mode: settings.content.proactiveGreetingMode,
            offerStatus: offer?.status ?? null,
          });
          return null;
        }
      }

      const entry = getProactiveGreetingCache(greetingSlug, greetingPathname);
      if (!entry?.shouldShow || entry.greetingPhrases.length === 0) {
        logGreetingOffer(reason, 'skip - no ready greeting', {
          slug: greetingSlug,
          pathname: greetingPathname,
          status: entry?.status,
          shouldShow: entry?.shouldShow,
          phrases: entry?.greetingPhrases.length ?? 0,
        });
        return null;
      }

      markGreetingOfferShownFromEntry(entry);
      acknowledgeLauncherGreetingPhrases(
        entry.slug,
        entry.greetingPhrases.map((phrase) => phrase.payload),
      );
      const greetingId = resolveGreetingIdFromEntry(entry);
      // Still show pending bubbles even if greeting_id was committed earlier —
      // send path stays idempotent; trailing match hides once phrases are in thread.

      const shownAt =
        options?.forceShownAt ??
        getGreetingOfferState(greetingSlug, greetingPathname)?.shownAt ??
        Date.now();

      let lastHistoryMs = 0;
      for (const message of messages) {
        const ts = Date.parse(message.createdAt);
        if (Number.isFinite(ts)) lastHistoryMs = Math.max(lastHistoryMs, ts);
      }
      const baseTimeMs = Math.max(shownAt, lastHistoryMs + 1_000);

      const next: PendingChatOffer = {
        pathname: greetingPathname,
        phrases: entry.greetingPhrases,
        baseTimeMs,
        greetingId,
      };
      setPendingChatOffer((prev) => {
        if (
          prev &&
          prev.greetingId === greetingId &&
          prev.pathname === greetingPathname
        ) {
          logGreetingOffer(reason, 'keep existing pending', {
            greetingId,
            phraseCount: prev.phrases.length,
            baseTimeMs: prev.baseTimeMs,
          });
          return prev;
        }
        logGreetingOffer(reason, 'set pending', {
          greetingId,
          pathname: greetingPathname,
          phraseCount: next.phrases.length,
          baseTimeMs,
          lastHistoryMs,
          committed: isGreetingIdCommitted(greetingSlug, greetingId),
          previews: next.phrases.map((p) => p.payload.slice(0, 60)),
        });
        return next;
      });
      return next;
    },
    [effectiveThreadId, greetingPathname, greetingSlug, logGreetingOffer, messages, settings.content.proactiveGreetingMode],
  );

  useEffect(() => {
    setPendingChatOffer((prev) =>
      prev && prev.pathname !== greetingPathname ? null : prev,
    );
  }, [greetingPathname]);

  // Drop pending UI once phrases are already in the thread (post-commit refetch).
  // Match anywhere — not only trailing — because stage may append `history` after
  // the AI reply. Do not clear solely because greeting_id is committed (backend
  // may have ignored history).
  useEffect(() => {
    if (!pendingChatOffer || !greetingSlug) return;
    if (messagesContainPhraseSequence(messages, pendingChatOffer.phrases)) {
      logGreetingOffer('clear pending - already in thread', {
        greetingId: pendingChatOffer.greetingId,
        messageCount: messages.length,
      });
      setPendingChatOffer(null);
    }
  }, [greetingSlug, logGreetingOffer, messages, pendingChatOffer]);

  const pendingContextMessages =
    !isFirstDialog &&
    pendingChatOffer &&
    pendingChatOffer.pathname === greetingPathname
      ? pendingChatOffer.phrases
      : undefined;

  const pendingContextBaseTimeMs = pendingContextMessages
    ? pendingChatOffer?.baseTimeMs
    : undefined;

  useEffect(() => {
    logGreetingOffer('render flags', {
      chatOpen,
      isFirstDialog,
      effectiveThreadId,
      pathname: greetingPathname,
      pendingPhraseCount: pendingChatOffer?.phrases.length ?? 0,
      pendingContextCount: pendingContextMessages?.length ?? 0,
      welcomeCount: isFirstDialog ? chatWelcomePhrases.length : 0,
    });
  }, [
    chatOpen,
    chatWelcomePhrases.length,
    effectiveThreadId,
    greetingPathname,
    isFirstDialog,
    logGreetingOffer,
    pendingChatOffer,
    pendingContextMessages?.length,
  ]);

  useEffect(() => {
    if (!chatOpen || !greetingSlug) return;
    if (chatWelcomePhrases.length === 0) return;
    const entry = getProactiveGreetingCache(greetingSlug, greetingPathname);
    if (!entry) return;

    if (isFirstDialog) {
      markGreetingOfferShownFromEntry(entry);
      logGreetingOffer('first dialog - mark shown for welcome');
      return;
    }

    capturePendingOfferFromCache({ reason: 'chat-open' });
  }, [
    capturePendingOfferFromCache,
    chatOpen,
    chatWelcomePhrases.length,
    greetingPathname,
    greetingSlug,
    isFirstDialog,
    logGreetingOffer,
  ]);

  const handleAutoOpenChat = useCallback(() => {
    if (chatOpen) return;
    capturePendingOfferFromCache({
      forceShownAt: Date.now(),
      reason: 'auto-open',
    });
    setChatOpen(true, 'greeting_auto_open');
  }, [capturePendingOfferFromCache, chatOpen, setChatOpen]);

  const {
    popupVisible: autoPopupVisible,
    popupMessage: autoPopupMessage,
    popupCtaText: autoPopupCtaText,
    popupKind: autoPopupKind,
    dismissPopup: dismissAutoPopup,
    acceptPopup: acceptAutoPopup,
  } = useContextPopups({
    enabled: autoPopupsEnabled && !embedded,
    chatOpen,
    apiBase,
    mock,
    onAutoOpenChat: settings.content.proactiveGreetingAutoOpenChat
      ? handleAutoOpenChat
      : undefined,
  });

  const popupVisible = popupVisibleProp ?? autoPopupVisible;
  const popupText = popupMessage ?? autoPopupMessage;
  const popupCta = autoPopupCtaText;
  const showPopupQuickReplies =
    autoPopupKind !== 'exit-intent' && quickReplies.length > 0;
  const showTyping = isTyping || isBotTyping;
  const hasManagerInThread = messages.some((message) => message.role === 'manager');
  const effectiveShowManager = showManager || hasManagerInThread;

  // When popup appears, capture offer so CTA / launcher opens chat with greeting.
  useEffect(() => {
    if (!popupVisible || chatOpen) return;
    capturePendingOfferFromCache({
      forceShownAt: Date.now(),
      reason: 'popup-visible',
    });
  }, [capturePendingOfferFromCache, chatOpen, popupVisible]);

  const closePopup = () => {
    onPopupClose?.();
    if (autoPopupsEnabled) {
      dismissAutoPopup();
    }
    logGreetingOffer('popup dismissed - clear pending');
    setPendingChatOffer(null);
  };

  const openChat = (source?: ChatOpenSource) => {
    // `onClick={openChat}` would pass a SyntheticEvent as `source` — only accept real ChatOpenSource strings.
    const explicitSource = typeof source === 'string' ? source : undefined;
    const popupSource: ChatOpenSource =
      explicitSource ??
      (autoPopupKind === 'exit-intent'
        ? 'popup_exit_intent'
        : autoPopupKind === 'context-ai'
          ? 'popup_context_ai'
          : 'launcher');

    if (autoPopupKind) {
      reportWidgetEvent('popup_cta_clicked', {
        kind: autoPopupKind,
        pathname:
          typeof window !== 'undefined' ? window.location.pathname : '/',
      });
      reportWidgetEvent('popup_converted_to_chat', {
        kind: autoPopupKind,
        pathname:
          typeof window !== 'undefined' ? window.location.pathname : '/',
        source: popupSource,
      });
      markPopupConvertedToChat();
    }

    if (autoPopupsEnabled && autoPopupKind) {
      acceptAutoPopup();
      capturePendingOfferFromCache({
        forceShownAt: Date.now(),
        reason: 'popup-cta',
      });
    } else {
      closePopup();
    }
    setChatOpen(true, popupSource);
  };

  const handleQuickReply = useCallback(
    (quickReply: ProactiveGreetingQuickReply, source: 'launcher' | 'popup') => {
      const popupSource: ChatOpenSource =
        autoPopupKind === 'exit-intent'
          ? 'popup_exit_intent'
          : autoPopupKind === 'context-ai'
            ? 'popup_context_ai'
            : 'launcher';
      const explicitSource: ChatOpenSource =
        source === 'popup' || autoPopupKind ? popupSource : 'launcher';

      if (autoPopupKind) {
        reportWidgetEvent('popup_cta_clicked', {
          kind: autoPopupKind,
          pathname:
            typeof window !== 'undefined' ? window.location.pathname : '/',
        });
        reportWidgetEvent('popup_converted_to_chat', {
          kind: autoPopupKind,
          pathname:
            typeof window !== 'undefined' ? window.location.pathname : '/',
          source: explicitSource,
        });
        markPopupConvertedToChat();
      }

      if (autoPopupsEnabled && autoPopupKind) {
        acceptAutoPopup();
      } else if (source === 'popup') {
        closePopup();
      }

      capturePendingOfferFromCache({
        forceShownAt: Date.now(),
        reason: source === 'popup' ? 'popup-quick-reply' : 'launcher-quick-reply',
      });
      setChatOpen(true, explicitSource);
      void send(quickReply.message);
    },
    [
      acceptAutoPopup,
      autoPopupKind,
      autoPopupsEnabled,
      capturePendingOfferFromCache,
      closePopup,
      send,
      setChatOpen,
    ],
  );

  const handleSend = useCallback(
    (text: string) => {
      void send(text);
    },
    [send],
  );

  const handleRetry = useCallback(
    (clientId: string) => {
      void retry(clientId);
    },
    [retry],
  );

  const position = settings.launcher.position;
  const isMobile = useIsMobileViewport(settings.window.mobileBreakpointPx);
  const mobileMode = settings.launcher.mobileMode;
  const useDraggable = !embedded && isMobile && mobileMode === 'draggable';
  const useEdge = !embedded && isMobile && mobileMode === 'edge';
  const [launcherRect, setLauncherRect] = useState<LauncherRect | null>(null);

  const popupPlacementStyle = useMemo(() => {
    if (!popupVisible || chatOpen) return undefined;
    if (!useDraggable && !useEdge) return undefined;
    const size = settings.launcher.sizePx;
    const fallbackPos = expandedLauncherPos(
      position,
      size,
      settings.launcher.offsetX,
      settings.launcher.offsetY,
    );
    const rect =
      launcherRect ??
      (useEdge
        ? {
            x: position.endsWith('_left') ? 0 : fallbackPos.x,
            y: fallbackPos.y,
            w: 40,
            h: 44,
          }
        : { x: fallbackPos.x, y: fallbackPos.y, w: size, h: size });
    return resolveGreetingPlacement(rect, useEdge ? 'edge' : 'quadrant').wrap;
  }, [
    chatOpen,
    launcherRect,
    popupVisible,
    position,
    settings.launcher.offsetX,
    settings.launcher.offsetY,
    settings.launcher.sizePx,
    useDraggable,
    useEdge,
  ]);

  const rootClass = embedded
    ? 'teo-widget teo-stack'
    : [
        'teo-widget',
        'teo-root',
        `teo-root--${position}`,
        chatOpen ? 'teo-root--chat-open' : '',
        useDraggable || useEdge ? 'teo-root--mobile-free' : '',
      ]
        .filter(Boolean)
        .join(' ');

  const style = cssVars as CSSProperties;

  const openFromLauncher = useCallback(() => {
    setChatOpen(true, 'launcher');
  }, [setChatOpen]);

  return (
    <div ref={widgetRootRef} className={rootClass} style={style}>
      <div className={embedded ? undefined : 'teo-stack'}>
        <ChatWindow
          visible={chatOpen}
          onClose={() => setChatOpen(false)}
          messages={messages}
          showManager={effectiveShowManager}
          showWelcome={showWelcome && isFirstDialog}
          welcomeMessages={isFirstDialog ? chatWelcomePhrases : undefined}
          pendingContextMessages={pendingContextMessages}
          pendingContextBaseTimeMs={pendingContextBaseTimeMs}
          knownGreetingPhrases={knownGreetingPhrases}
          historyStatus={resolvedHistoryStatus}
          onHistoryRetry={retryHistory}
          isTyping={showTyping}
          isRecording={isRecording}
          onSend={handleSend}
          onRetry={handleRetry}
          onToggleRecording={onToggleRecording}
          connectionBanner={connectionBanner}
          popupDebug={popupDebug}
        />

        {popupVisible && !chatOpen && (
          <ContextPopup
            message={popupText}
            ctaText={popupCta}
            quickReplies={showPopupQuickReplies ? quickReplies : undefined}
            showCta={!showPopupQuickReplies}
            onOpenChat={openChat}
            onQuickReply={(quickReply) => handleQuickReply(quickReply, 'popup')}
            onClose={closePopup}
            placementStyle={popupPlacementStyle}
          />
        )}

        {!chatOpen && useDraggable && (
          <DraggableLauncher
            position={position}
            sizePx={settings.launcher.sizePx}
            offsetX={settings.launcher.offsetX}
            offsetY={settings.launcher.offsetY}
            unreadCount={launcherUnread}
            onOpen={openFromLauncher}
            onRectChange={setLauncherRect}
          />
        )}

        {!chatOpen && useEdge && (
          <EdgeDockTab
            position={position}
            offsetY={settings.launcher.offsetY}
            unreadCount={launcherUnread}
            onOpen={openFromLauncher}
            onRectChange={setLauncherRect}
          />
        )}

        {!chatOpen && !useDraggable && !useEdge && (
          <LauncherButton
            unreadCount={launcherUnread}
            onClick={() => setChatOpen(true, 'launcher')}
          />
        )}
      </div>
    </div>
  );
}

export function WidgetShell({
  rawSettings,
  initialLocale,
  messages,
  threadId = null,
  sendMessage,
  sendImageMessage,
  apiBase,
  reverbConfig,
  initialMessages,
  showWelcome,
  mock,
  ...inner
}: WidgetShellProps) {
  const isControlled = messages !== undefined;
  const defaultShowWelcome = showWelcome ?? true;

  return (
    <WidgetProvider rawSettings={rawSettings} initialLocale={initialLocale}>
      <ChatProvider
        messages={isControlled ? messages : undefined}
        initialMessages={isControlled ? undefined : (initialMessages ?? [])}
        threadId={threadId}
        sendMessage={isControlled ? undefined : (sendMessage ?? defaultSendMessage)}
        sendImageMessage={
          isControlled ? undefined : (sendImageMessage ?? defaultSendImageMessage)
        }
        apiBase={apiBase}
      >
        <WidgetShellInner
          {...inner}
          showWelcome={defaultShowWelcome}
          apiBase={apiBase}
          threadId={threadId}
          reverbConfig={reverbConfig}
          mock={mock}
          loadThreadHistory={!isControlled && Boolean(apiBase) && !mock}
        />
      </ChatProvider>
    </WidgetProvider>
  );
}

export function WidgetPreview(
  props: WidgetShellProps & { previewHostClassName?: string },
) {
  const { previewHostClassName, ...shellProps } = props;
  return (
    <>
      <style>{widgetCss}</style>
      <div
        className={['teo-preview-host', previewHostClassName]
          .filter(Boolean)
          .join(' ')}
      >
        <WidgetShell {...shellProps} />
      </div>
    </>
  );
}

export { widgetCss };
